import { useEffect, useMemo, useRef, useState } from 'react';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { getBallot, getCurrentRound, getMe, getRoster, getTracks, putBallot } from '../../core/api';
import { avatarStyle, gradFor, initials, paceLabel, closesIn } from '../format';
import {
  budgetSpent, canStep, draftFromEntries, draftToEntries, guessesComplete,
  isSubmittable, pointsComplete, takenGuesses, vetoCount, vetoEligible, type Draft,
} from '../../core/ballot';
import type { Ballot, CurrentRound, Me, RosterMember, TracksResponse } from '../../core/types';
import s from './RoundVote.module.css';

// One page, one job: assemble your whole ballot for the open round. The three
// dimensions the server scores — points, who-submitted guess, one optional veto —
// share the same track list via a mode switch, so every entry stays on screen.
// Budget/guess/veto rules come from ballot.ts (the API re-validates them all).

type Mode = 'points' | 'guess' | 'veto';
const MODES: { id: Mode; label: string }[] = [
  { id: 'points', label: 'Points' },
  { id: 'guess', label: 'Guess' },
  { id: 'veto', label: 'Veto' },
];

const PlayIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;

// Guess picker: a face-first replacement for the old <select> — each candidate shows
// their photo (or initials), with the ones already named on another track greyed as
// "taken". Closes on pick, outside-click, or Escape; only one open at a time per row.
function GuessPicker({ candidates, value, taken, onChange }: {
  candidates: RosterMember[]; value: number | null; taken: Set<number>; onChange: (uid: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const sel = candidates.find((c) => c.user_id === value) ?? null;
  return (
    <div className={s.gp} ref={wrap}>
      <button type="button" className={`${s.gptrigger}${sel ? ' ' + s.gpon : ''}`} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {sel ? (
          <>
            <span className={s.gpav} style={avatarStyle(sel.avatar_url, sel.user_id)}>{sel.avatar_url ? '' : initials(sel.display_name)}</span>
            <span className={s.gpname}>{sel.display_name}</span>
          </>
        ) : <span className={s.gpwho}>Who?</span>}
        <span className={s.gpchev} aria-hidden>▾</span>
      </button>
      {open ? (
        <div className={s.gpmenu} role="listbox">
          <button type="button" className={s.gpitem} onClick={() => { onChange(null); setOpen(false); }}>
            <span className={s.gpav} style={{ background: 'var(--sur3)' }} />
            <span className={s.gpname}>Nobody yet</span>
          </button>
          {candidates.map((c) => {
            const disabled = taken.has(c.user_id) && c.user_id !== value;
            const selected = c.user_id === value;
            return (
              <button key={c.user_id} type="button" role="option" aria-selected={selected} disabled={disabled}
                className={`${s.gpitem}${selected ? ' ' + s.gpsel : ''}`} onClick={() => { onChange(c.user_id); setOpen(false); }}>
                <span className={s.gpav} style={avatarStyle(c.avatar_url, c.user_id)}>{c.avatar_url ? '' : initials(c.display_name)}</span>
                <span className={s.gpname}>{c.display_name}</span>
                {disabled ? <span className={s.gptaken}>taken</span> : selected ? <span className={s.gptick}>✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function RoundVote() {
  const nav = useNav();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const roundId = round.data?.round_id ?? null;
  const tracksR = useAsync<TracksResponse | null>(() => (roundId ? getTracks(roundId) : Promise.resolve(null)), [roundId]);
  const ballotR = useAsync<Ballot | null>(() => (roundId ? getBallot(roundId) : Promise.resolve(null)), [roundId]);
  const roster = useAsync<RosterMember[]>(getRoster, []);
  const me = useAsync<Me>(getMe, []);

  const [mode, setMode] = useState<Mode>('points');
  const [playing, setPlaying] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const votable = useMemo(
    () => (tracksR.data?.tracks ?? []).filter((t) => !t.is_mine).sort((a, b) => a.display_order - b.display_order),
    [tracksR.data],
  );

  useEffect(() => {
    if (!tracksR.data || !ballotR.data) return;
    setDraft(draftFromEntries(ballotR.data.entries, votable.map((t) => t.submission_id)));
    setSaved(ballotR.data.submitted);
  }, [tracksR.data, ballotR.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const eco = ballotR.data?.ruleset ?? null;
  const myId = me.data?.user?.user_id ?? null;
  const candidates = useMemo(
    () => (roster.data ?? []).filter((r) => r.user_id !== myId),
    [roster.data, myId],
  );

  // Wait only for the round itself here. The phase gate below must run BEFORE we wait
  // on the ballot/tracks — a SUBMITTING round never yields a ballot (423), so gating on
  // `draft` first would hang forever at "Loading the round…".
  if (round.loading) {
    return <Shell><div className={s.state}>Loading the round…</div></Shell>;
  }
  // Not in VOTING → explain what's happening instead of spinning.
  if (!round.data || round.data.phase !== 'VOTING') {
    const raw = round.data?.phase === 'SUBMITTING' ? closesIn(round.data.submission_deadline) : null;
    const opensIn = raw && raw.startsWith('closes ') ? raw.slice('closes '.length) : null; // "in 3d"
    const line =
      round.data?.phase === 'SUBMITTING'
        ? `This round is still gathering submissions. Voting opens when they close${opensIn ? ` — ${opensIn}` : ''}, unless they’re closed early.`
        : round.data?.phase === 'CLOSED'
          ? 'This round is done — the results are in.'
          : round.data
            ? 'This round hasn’t opened yet.'
            : 'Check back when the next round opens.';
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>Vote</span></div>
        <div className={s.state}>Voting isn’t open yet.<br />{line}</div>
      </Shell>
    );
  }
  // VOTING from here — now it's safe to wait on the ballot/tracks/draft.
  if (tracksR.loading || ballotR.loading || !draft || !eco) {
    return <Shell><div className={s.state}>Loading the round…</div></Shell>;
  }

  const patch = (id: number, fn: (d: { points: number; guessed_user_id: number | null; is_veto: boolean }) => void) =>
    setDraft((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      const dim = { ...next.get(id)! };
      fn(dim);
      next.set(id, dim);
      return next;
    });

  const step = (id: number, d: number) => { if (canStep(draft, id, d, eco)) { patch(id, (dim) => { dim.points += d; }); setSaved(false); } };
  const setGuess = (id: number, uid: number | null) => { patch(id, (dim) => { dim.guessed_user_id = uid; }); setSaved(false); };
  const toggleVeto = (id: number) => { patch(id, (dim) => { dim.is_veto = !dim.is_veto; }); setSaved(false); };

  const spent = budgetSpent(draft, eco);
  const named = [...draft.values()].filter((d) => d.guessed_user_id != null).length;
  const vetoes = vetoCount(draft);
  const ready = isSubmittable(draft, eco);

  const status =
    mode === 'points' ? `${spent} of ${eco.budget} points spent${pointsComplete(draft, eco) ? ' ✓' : ''}`
      : mode === 'guess' ? `${named} of ${votable.length} named${guessesComplete(draft) ? ' ✓' : ''}`
        : vetoes ? 'One veto cast ✓' : 'No veto — optional';

  async function submit(final: boolean) {
    if (!roundId || !draft) return;
    setBusy(true); setErr(null);
    try {
      await putBallot(roundId, draftToEntries(draft), final);
      setSaved(final);
    } catch {
      setErr('Could not save — try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>Season 1 · voting</span>
        <button className={s.menu} aria-label="Menu" onClick={() => nav?.go('home')}>⋯</button>
      </div>
      <div className={s.vhead}>
        <h2>{round.data.theme} <span className={s.badge} style={{ background: `var(--${tracksR.data?.media_type ?? 'audio'})` }}>{tracksR.data?.media_type ?? 'audio'}</span></h2>
        <p>{[`${votable.length} entries to judge`, paceLabel(round.data.pace), closesIn(round.data.voting_deadline)].filter(Boolean).join(' · ')} · your track is set aside</p>
      </div>

      <div className={s.modes}>
        {MODES.map((m) => (
          <button key={m.id} className={`${s.modebtn}${mode === m.id ? ' ' + s.on : ''}`} onClick={() => setMode(m.id)}>{m.label}</button>
        ))}
      </div>

      <div className={s.budget}>
        <div className={s.budgetrow}>
          <span className={s.l}>{mode === 'points' ? 'Spend your points' : mode === 'guess' ? 'Name each submitter' : 'One veto, optional'}</span>
          <span className={s.pill}>{status}</span>
        </div>
        {mode === 'points' ? <div className={s.meter}><i style={{ width: `${Math.min(100, (spent / eco.budget) * 100)}%` }} /></div> : null}
      </div>

      <div className={s.list}>
        {votable.map((t) => {
          const dim = draft.get(t.submission_id)!;
          const taken = takenGuesses(draft, t.submission_id);
          const canVeto = vetoEligible(dim) && (vetoes < 1 || dim.is_veto);
          return (
            <div key={t.submission_id} className={`${s.row}${playing === t.submission_id ? ' ' + s.playing : ''}`}>
              <span className={s.cover} style={{ background: gradFor(t.submission_id) }} onClick={() => setPlaying(t.submission_id)} role="button" aria-label={`Play ${t.title}`}>
                <span className={s.play}><PlayIcon /></span>
                {tracksR.data?.media_type ? <span className={s.pin} style={{ background: `var(--${tracksR.data.media_type})` }} /> : null}
              </span>
              <span className={s.en}><b>{t.title}</b><small>{t.subtitle ?? 'submitter hidden'}</small></span>

              {mode === 'points' ? (
                <span className={s.step}>
                  <button onClick={() => step(t.submission_id, -1)} disabled={!canStep(draft, t.submission_id, -1, eco)} aria-label="Less">–</button>
                  <span className={s.v}>{dim.points}</span>
                  <button className={s.plus} onClick={() => step(t.submission_id, 1)} disabled={!canStep(draft, t.submission_id, 1, eco)} aria-label="More">+</button>
                </span>
              ) : mode === 'guess' ? (
                <GuessPicker candidates={candidates} value={dim.guessed_user_id} taken={taken} onChange={(uid) => setGuess(t.submission_id, uid)} />
              ) : (
                <button className={`${s.veto}${dim.is_veto ? ' ' + s.von : ''}`} disabled={!canVeto} onClick={() => toggleVeto(t.submission_id)}>{dim.is_veto ? 'Vetoed' : 'Veto'}</button>
              )}
            </div>
          );
        })}
      </div>

      <div className={s.foot}>
        <span className={s.leftinfo}>{saved ? <b className={s.ok}>Ballot submitted ✓</b> : err ? <b className={s.errtx}>{err}</b> : ready ? 'Everything set' : 'Points, guesses, and (optionally) a veto'}</span>
        {saved ? (
          <button className={s.submit} onClick={() => submit(false)} disabled={busy}>Edit</button>
        ) : (
          <>
            {!ready ? <button className={s.save} onClick={() => submit(false)} disabled={busy}>Save</button> : null}
            <button className={s.submit} onClick={() => submit(true)} disabled={busy || !ready}>Submit ballot</button>
          </>
        )}
      </div>
    </Shell>
  );
}
